// KI-Raumvorschläge: Sobald eine Vorschau dekodiert ist, geht ein kleines Vorschaubild
// (max. 512 px) über das eigene Backend an Claude. Das Ergebnis füllt das Namensfeld vor,
// solange die Person dort nichts selbst eingetragen hat.
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, fetchMe, suggestRoom, type GetIdToken } from "../stats/api";

const THUMB_MAX = 512;
const PARALLEL = 3;
// Bei diesen Fehlern lohnt kein weiterer Versuch in diesem Durchgang.
const FATAL = new Set(["disabled", "no_key", "key_invalid", "no_workspace", "no_credit"]);

export interface RoomSuggestionState {
  enabled: boolean;
  suggestions: Record<string, string>;
  pending: Set<string>;
  done: number;
  total: number;
  error: string | null;
}

export function useRoomSuggestions(getIdToken: GetIdToken | null) {
  const [enabled, setEnabled] = useState(false);
  const [suggestions, setSuggestions] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const queue = useRef<Array<{ name: string; image: Promise<string> }>>([]);
  const running = useRef(0);
  const generation = useRef(0); // verwirft Antworten aus einem vorherigen Ordner
  const stopped = useRef(false);

  useEffect(() => {
    if (!getIdToken) return;
    fetchMe(getIdToken)
      .then((me) => setEnabled(me.aiEnabled))
      .catch(() => setEnabled(false));
    // getIdToken ist über die Sitzung stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pump = useCallback(() => {
    if (!getIdToken) return;
    while (running.current < PARALLEL && queue.current.length > 0 && !stopped.current) {
      const job = queue.current.shift()!;
      const gen = generation.current;
      running.current += 1;
      void (async () => {
        try {
          const room = await suggestRoom(getIdToken, await job.image);
          if (gen === generation.current) setSuggestions((s) => ({ ...s, [job.name]: room }));
        } catch (err) {
          if (gen !== generation.current) return;
          const code = err instanceof ApiError ? err.code : "";
          if (FATAL.has(code)) {
            stopped.current = true;
            queue.current = [];
            setPending(new Set());
          }
          setError(code === "disabled" ? null : aiErrorText(code));
        } finally {
          // Nach reset() gehören laufende Anfragen zum alten Durchgang und zählen nicht mehr.
          if (gen === generation.current) {
            running.current -= 1;
            setDone((d) => d + 1);
            setPending((p) => {
              const next = new Set(p);
              next.delete(job.name);
              return next;
            });
            pump();
          }
        }
      })();
    }
  }, [getIdToken]);

  // Aus der Galerie: Vorschau ist fertig -> Vorschaubild erzeugen und einreihen.
  const onPreview = useCallback(
    (name: string, bitmap: ImageBitmap) => {
      if (!enabled || !getIdToken || stopped.current) return;
      // Synchron zeichnen, solange das Bitmap sicher offen ist.
      const s = Math.min(1, THUMB_MAX / Math.max(bitmap.width, bitmap.height));
      const canvas = new OffscreenCanvas(Math.round(bitmap.width * s), Math.round(bitmap.height * s));
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const image = canvas.convertToBlob({ type: "image/jpeg", quality: 0.8 }).then(toBase64);
      queue.current.push({ name, image });
      setTotal((t) => t + 1);
      setPending((p) => new Set(p).add(name));
      pump();
    },
    [enabled, getIdToken, pump],
  );

  const reset = useCallback(() => {
    generation.current += 1;
    queue.current = [];
    running.current = 0;
    stopped.current = false;
    setSuggestions({});
    setPending(new Set());
    setDone(0);
    setTotal(0);
    setError(null);
  }, []);

  const state: RoomSuggestionState = { enabled, suggestions, pending, done, total, error };
  return { state, onPreview, reset };
}

// Klare Ursache für alle Nutzer – und der Hinweis, dass es ohne KI genauso weitergeht.
const FALLBACK = "Namen einfach im Dropdown wählen oder selbst eintippen – das Skalieren funktioniert ganz normal.";
function aiErrorText(code: string): string {
  const reason: Record<string, string> = {
    no_credit: "Das KI-Guthaben ist aufgebraucht.",
    key_invalid: "Der KI-Schlüssel ist abgelaufen oder ungültig.",
    no_workspace: "Der KI-Zugang ist nicht richtig eingerichtet.",
    no_key: "Auf dem Server ist kein KI-Schlüssel hinterlegt.",
    rate_limited: "Die KI ist gerade ausgelastet.",
    overloaded: "Die KI ist gerade überlastet.",
  };
  return `⚠️ KI-Raumerkennung nicht verfügbar: ${reason[code] ?? "Die KI ist gerade nicht erreichbar."} ${FALLBACK} Bitte Christian Bescheid geben.`;
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
